import { useFeatureFlags } from '@brightdrop/feature-flags-client';
import { DateTime } from '@brightdrop/localization-core';

import CSPlatformDateTime from '~/common/components/date/CSPlatformDateTime';
import { LOCALIZATION_FEATURE_FLAG } from '~/common/models/featureFlags.model';
import { toDateStringWithTimezone } from '~/common/utils/date-time.utils';

/** Past a day the age stops locating an event, and the reader wants the day itself instead. */
const RELATIVE_WINDOW_MS = 24 * 60 * 60 * 1000;

const isWithinRelativeWindow = (instant: string): boolean => {
  const elapsed = Date.now() - new Date(instant).getTime();

  return Number.isFinite(elapsed) && elapsed < RELATIVE_WINDOW_MS;
};

/**
 * When a notification fired.
 *
 * With the flag on the stamp follows the reader's resolved preferences: how long ago for the last
 * day, then their own field order, clock and zone beyond it. Off, it keeps the legacy stamp, whose
 * weekday and month names are written in English whoever is reading.
 */
const NotificationDateTime = ({
  value,
}: {
  value?: string | null;
}): JSX.Element => {
  const { getFlag } = useFeatureFlags();
  /* The payload carries no offset, so it is read as UTC rather than as the host's local time. */
  const instant = value ? toDateStringWithTimezone(value) : null;

  if (!getFlag(LOCALIZATION_FEATURE_FLAG)) {
    return <CSPlatformDateTime date={instant} />;
  }

  return (
    <DateTime
      style={
        instant && isWithinRelativeWindow(instant) ? 'relativeTime' : 'dateTime'
      }
      value={instant}
      withZone
    />
  );
};

export default NotificationDateTime;
